/**
 * adminSettingsStore — Platform settings slice of the admin store.
 *
 * Endpoints:
 *   GET   /api/admin/settings
 *   PATCH /api/admin/settings
 */
import { API_URL } from "../../constants";
import { toDisplayString } from "../../utils/toDisplayString";
import { authHeaders, resolveToken } from "./adminUtils";

export const settingsInitialState = {
  settings: null,
  settingsLoading: false,
  settingsError: null,
  settingsMutationLoading: false,
  settingsMutationError: null,
};

async function apiRequest(get, path, { method = "GET", body } = {}) {
  const response = await fetch(`${API_URL}${path}`, {
    method,
    headers: authHeaders(resolveToken(get)),
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });

  const text = await response.text();
  let data = {};
  if (text) {
    const contentType = response.headers.get("content-type") || "";
    const trimmed = text.trim();
    if (contentType.includes("application/json") || trimmed.startsWith("{") || trimmed.startsWith("[")) {
      try {
        data = JSON.parse(text);
      } catch {
        data = {};
      }
    }
  }

  if (!response.ok || (data && data.error)) {
    throw new Error(
      toDisplayString(data?.error ?? data?.message, `Request failed (${response.status})`)
    );
  }

  return data;
}

export function createSettingsActions(set, get) {
  return {
    fetchSettings: async () => {
      set({ settingsLoading: true, settingsError: null });
      try {
        const data = await apiRequest(get, "/api/admin/settings");
        const settings = data?.data ?? data ?? null;
        set({ settingsLoading: false, settings });
        return { success: true, settings };
      } catch (err) {
        const message = toDisplayString(err.message, "Failed to fetch settings");
        set({ settingsLoading: false, settingsError: message });
        return { success: false, error: message };
      }
    },

    updateSettings: async (payload) => {
      set({ settingsMutationLoading: true, settingsMutationError: null });
      try {
        const data = await apiRequest(get, "/api/admin/settings", {
          method: "PATCH",
          body: payload,
        });
        const settings = data?.data ?? data ?? null;
        set({
          settingsMutationLoading: false,
          settingsMutationError: null,
          settings,
          successMessage: "Settings updated",
        });
        return { success: true, settings };
      } catch (err) {
        const message = toDisplayString(err.message, "Failed to update settings");
        set({ settingsMutationLoading: false, settingsMutationError: message });
        return { success: false, error: message };
      }
    },
  };
}

const settingsActions = createSettingsActions;
export default settingsActions;
